import * as _ from 'lodash';
import { Document } from 'src/app/people/models/document';
import { BehaviorSubject, Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { ApiVersions } from 'src/app/shared/services/api-version';
import { UploadBaseService } from './upload-base.service';
import { map } from 'rxjs/operators';
import { isTodayPastThreshold } from '../utils/date-utils';

@Injectable()
export class AWSService extends UploadBaseService {
  public readonly personDocuments = new BehaviorSubject<Document[]>([]);

  private awsUsersBaseUrl(userId: string) {
    return `users/${userId}`;
  }

  private awsTeamsBaseUrl(teamId: string) {
    return `teams/${teamId}`;
  }

  listBucket(userId: string): Document[] {
    let documents = new Array<Document>();
    let obs = this.get(this.awsUsersBaseUrl(userId).concat('/documents'), true, ApiVersions.v1_0);

    obs.subscribe((result) => {
      if (!result) return;
      _.each(result, function (upload: any) {
        let documentName = upload.key.split(`${userId}/`)[1];
        documents.push(new Document(upload.key, documentName, upload.lastModified));
      });
      this.personDocuments.next(documents);
    });

    return documents;
  }

  listExportDownloads(): Observable<any> {
    return this.get('exports', true, ApiVersions.v0_1).pipe(
      map((s3Objects) => {
        return s3Objects
          .filter((s3Object) => {
            return !isTodayPastThreshold(s3Object.lastModified, 14);
          })
          .map((s3Object) => {
            return {
              ...s3Object,
              filename: s3Object.key.substr(s3Object.key.lastIndexOf('/') + 1),
            };
          })
          .sort((obj1, obj2) => {
            return obj2.lastModified < obj1.lastModified
              ? -1
              : obj2.lastModified > obj1.lastModified
              ? 1
              : 0;
          });
      })
    );
  }

  getObjectUrl(key: string): Observable<any> {
    const encodedKey = encodeURIComponent(key);
    return this.get('signed-url'.concat(`?key=${encodedKey}`), false, ApiVersions.v0_1);
  }

  getUserObjectUrl(userId: string, key: string): Observable<any> {
    let encodedKey = encodeURIComponent(key);
    let path = this.awsUsersBaseUrl(userId).concat('/documents').concat('/signed-url');
    return this.get(path.concat(`?key=${encodedKey}`), false, ApiVersions.v1_0);
  }

  getUserCredentialObjectUrl(userId: string, key: string): Promise<{ key: string; url: string }> {
    let encodedKey = encodeURIComponent(key);
    let path = this.awsUsersBaseUrl(userId).concat('/evidences').concat('/signed-url');
    return this.get(path.concat(`?key=${encodedKey}`), false, ApiVersions.v1_0).toPromise();
  }

  getTeamObjectUrl(teamId: string, key: string): Observable<any> {
    let encodedKey = encodeURIComponent(key);
    let path = this.awsTeamsBaseUrl(teamId).concat('/documents').concat('/signed-url');
    return this.get(path.concat(`?key=${encodedKey}`), false, ApiVersions.v1_0);
  }

  uploadToS3(file, person): Observable<any> {
    let showMessage = true;
    let cmp = this;
    return super.doUpload(
      cmp.awsUsersBaseUrl(person.id).concat('/documents'),
      file,
      showMessage,
      ApiVersions.v1_0,
      () => (person.documents = cmp.listBucket(person.id))
    );
  }

  scanForVirus(file) {
    let showMessage = true;
    super.doScan('documents/scan', file, showMessage, ApiVersions.v0_1);
  }

  deleteFromS3(
    entityId: string,
    filePath: string,
    isTeamDocument: boolean = false
  ): Observable<any> {
    if (isTeamDocument) {
      return this.delete(
        this.awsTeamsBaseUrl(entityId).concat(`/documents/?key=${encodeURIComponent(filePath)}`),
        null,
        ApiVersions.v1_0
      );
    }

    return this.delete(
      this.awsUsersBaseUrl(entityId).concat(`/documents/?key=${encodeURIComponent(filePath)}`),
      null,
      ApiVersions.v1_0
    );
  }

  uploadToS3Generic(
    file,
    baseUrl,
    apiVersion: ApiVersions | undefined,
    showMessage = true
  ): Observable<any> {
    return super.doUpload(`${baseUrl}/attachment`, file, showMessage, apiVersion, null);
  }
}
